import { Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { isValidHexColor, normalizeHex } from '../../colors/hexColor';
import type { FriendSlotChip } from './friendSpotSelection';
import { ROW_PADDING_HORIZONTAL, styles } from './FriendSpotSlots.styles';
import { useScrollSlotIntoView } from './useScrollSlotIntoView';

export type FriendSpotSlotsProps = {
  /** One chip per booking slot (index 0 = member). See buildFriendSlotChips. */
  slots: FriendSlotChip[];
  activeIndex: number;
  errorSlotIndexes?: number[];
  selectedSpotColor?: string;
  onSlotPress: (index: number) => void;
};

export function FriendSpotSlots({
  slots,
  activeIndex,
  errorSlotIndexes = [],
  selectedSpotColor,
  onSlotPress
}: FriendSpotSlotsProps) {
  const { scrollRef, onLayout, onContentSizeChange, onChipLayout } = useScrollSlotIntoView(
    activeIndex,
    ROW_PADDING_HORIZONTAL
  );
  const errorSlotIndexSet = new Set(errorSlotIndexes);
  const resolvedSpotColor = isValidHexColor(selectedSpotColor)
    ? normalizeHex(selectedSpotColor)
    : undefined;
  return (
    <View style={styles.container} testID="friend-spot-slots">
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={Platform.OS === 'web'}
        contentContainerStyle={styles.row}
        onLayout={onLayout}
        onContentSizeChange={onContentSizeChange}
        testID="friend-spot-slots-scroll"
      >
        {slots.map(({ label, spotLabel }, index) => {
          const isActive = index === activeIndex;
          const hasSpot = spotLabel !== null;
          const hasError = errorSlotIndexSet.has(index);
          return (
            <Pressable
              key={`slot-${String(index)}`}
              onPress={() => {
                onSlotPress(index);
              }}
              onLayout={(e) => {
                onChipLayout(index, e);
              }}
              style={[
                styles.chip,
                hasSpot && styles.chipWithSpot,
                hasError && styles.chipError,
                isActive && styles.chipActive,
                isActive && resolvedSpotColor !== undefined && { borderColor: resolvedSpotColor }
              ]}
              accessibilityRole="button"
              aria-selected={isActive}
              testID={`friend-spot-slot-${String(index)}`}
            >
              <Text
                style={[styles.chipLabel, hasError && styles.chipLabelError]}
                numberOfLines={1}
                testID={`friend-spot-slot-${String(index)}-label`}
              >
                {label}
              </Text>
              {hasSpot && (
                <Text
                  style={[
                    styles.chipSpotLabel,
                    resolvedSpotColor !== undefined && { color: resolvedSpotColor }
                  ]}
                  numberOfLines={1}
                  testID={`friend-spot-slot-${String(index)}-spot`}
                >
                  {spotLabel}
                </Text>
              )}
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}
